import { action, observable, toJS } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { IOrderHistoryTableData } from '../../actions/order-history/get-order-history.action';
import { Separator } from '../../actions/user-preferences/get-localization.action';
import {
    LOCALIZATION_DEFAULT_LOCALE,
    LOCALIZATION_DEFAULT_PREFERRED_UNIT,
    LOCALIZATION_DEFAULT_TIMEZONE
} from '../../configuration/localization';
import { ILocalizationState } from '../../states/localization-state';
import { ErrorBoundary } from '../../utilities/global-error-boundary';
import { ALL_STATUS, ORDER_HISTORY_DATE_FILTER_TWO_YEARS } from '../../configuration/order-history';
import { getDashboardOrderHistoryAction, GetDashboardOrderHistoryInput, ILxsOrderOverviewData } from './lxs-order-overview.data';
import { ILxsOrderOverviewProps } from './lxs-order-overview.props.autogenerated';
import LxsOrderOverviewView from './lxs-order-overview.view';
import { DEMO_ORDERS } from './mocks/demo-orders';

const FALLBACK_LOCALIZATION_STATE: ILocalizationState = {
    numberLocale: {
        thousandSeparator: Separator.Comma,
        decimalSeparator: Separator.Dot
    },
    dateLocale: LOCALIZATION_DEFAULT_LOCALE,
    preferredUnit: LOCALIZATION_DEFAULT_PREFERRED_UNIT,
    timeZone: LOCALIZATION_DEFAULT_TIMEZONE
};

@observer
class LxsOrderOverview extends React.Component<ILxsOrderOverviewProps<ILxsOrderOverviewData>> {
    @observable private orders: IOrderHistoryTableData[] | undefined = undefined;
    @observable private isDataReady: boolean = false;
    @observable private hasError: boolean = false;

    public async componentDidMount(): Promise<void> {
        const user = this.props.context.request.user;
        const ctx = this.props.context.actionContext;
        if (!user?.isAuthenticated) {
            this.setDataReady();
            return;
        }

        if (this.props.data.localizationState) {
            await this.props.data.localizationState;
        }

        const serverPreloadedOrderHistory = this.props.data.getOrderHistory?.result;
        if (serverPreloadedOrderHistory) {
            if (serverPreloadedOrderHistory.error) {
                this.setHasError();
            } else {
                this.setOrders(serverPreloadedOrderHistory.Orders ?? []);
            }
            this.setDataReady();
            return;
        }

        try {
            const result = await getDashboardOrderHistoryAction(
                new GetDashboardOrderHistoryInput(ctx, ALL_STATUS, ORDER_HISTORY_DATE_FILTER_TWO_YEARS),
                ctx
            );
            if (result) {
                if (result.error) {
                    this.setHasError();
                } else {
                    this.setOrders(result.Orders ?? []);
                }
            }
        } catch {
            this.setHasError();
        }

        this.setDataReady();
    }

    @action
    private setOrders(orders: IOrderHistoryTableData[]): void {
        if (this.props.config.appendDemoOrders) {
            const maxOrders = this.props.config.maxOrders ?? 10;
            const realSlice = orders.slice(0, Math.max(0, maxOrders - DEMO_ORDERS.length));
            this.orders = toJS([...realSlice, ...DEMO_ORDERS]);
        } else {
            this.orders = toJS(orders);
        }
    }

    @action
    private setDataReady(): void {
        this.isDataReady = true;
    }

    @action
    private setHasError(): void {
        this.hasError = true;
    }

    public render(): JSX.Element {
        if (!this.props.context.request.user?.isAuthenticated) {
            return <></>;
        }

        const localizationState = toJS(this.props.data.localizationState?.result) ?? FALLBACK_LOCALIZATION_STATE;

        return (
            <LxsOrderOverviewView
                orders={this.orders}
                isDataReady={this.isDataReady}
                hasError={this.hasError}
                config={this.props.config}
                resources={this.props.resources}
                context={this.props.context}
                localizationState={localizationState}
            />
        );
    }
}

export default ErrorBoundary(LxsOrderOverview, 'lxs-order-overview');
